/**
 * Non-standard / Epicor labels → ISO 4217 for Intl.NumberFormat `{ currency }`.
 * EURO (and similar) map to EUR; Intl only accepts three-letter ISO codes.
 */
const CODE_ALIAS_TO_ISO = {
  YEN: 'JPY',
  JYN: 'JPY',
  YUAN: 'CNY',
  RMB: 'CNY',
  UAE: 'AED',
  EURO: 'EUR'
};

/**
 * Company / Epicor-supported ISO 4217 codes (minimum set requested + prior extras).
 *
 * Accepted non-ISO labels (e.g. EURO → EUR) live in CODE_ALIAS_TO_ISO above.
 */
const CORE_SUPPORTED_ISO_CODES = [
  'AED',
  'AUD',
  'CAD',
  'CNY',
  'EUR',
  'GBP',
  'HKD',
  'JOD',
  'JPY',
  'MYR',
  'QAR',
  'SAR',
  'SEK',
  'SGD',
  'USD'
];

const EXTENDED_SUPPORTED_ISO_CODES = [
  'AZN',
  'CHF',
  'CZK',
  'DKK',
  'EGP',
  'ISK',
  'MOP',
  'MXN',
  'NOK',
  'NZD',
  'THB',
  'TRY'
];

const SUPPORTED_ISO_CODES = new Set([
  ...CORE_SUPPORTED_ISO_CODES,
  ...EXTENDED_SUPPORTED_ISO_CODES
]);

/**
 * Normalize API / Epicor currency label toward ISO 4217 for Intl.
 * Returns null if unknown (caller should fall back).
 */
export function normalizeToIsoCurrency(code) {
  if (code == null) return null;
  const s = String(code).trim().toUpperCase();
  if (!s) return null;
  if (CODE_ALIAS_TO_ISO[s]) {
    const resolved = CODE_ALIAS_TO_ISO[s];
    return SUPPORTED_ISO_CODES.has(resolved) ? resolved : null;
  }
  if (SUPPORTED_ISO_CODES.has(s)) return s;
  return null;
}

const formatterCache = new Map();

function getFormatter(isoCurrency) {
  if (formatterCache.has(isoCurrency)) return formatterCache.get(isoCurrency);
  let nf;
  try {
    nf = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: isoCurrency
    });
    // Verify constructor accepted (Intl may not throw for all invalid codes in some engines)
    nf.format(0);
  } catch {
    nf = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD'
    });
  }
  formatterCache.set(isoCurrency, nf);
  return nf;
}

/** Format monetary value; nullish amount shows em dash */
export function formatPurchaseCurrency(amount, currencyCode) {
  if (amount == null) return '—';
  const iso = normalizeToIsoCurrency(currencyCode) || 'USD';
  try {
    return getFormatter(iso).format(amount);
  } catch {
    return getFormatter('USD').format(amount);
  }
}
